import React, { useEffect, useMemo, useState } from "react";
import {
	checkAdminUserOrderStatus,
	createAdminPaymentLink,
	deleteUnpaidAdminUserOrder,
	getAdminUserOrders,
	getPhotos,
	getSelfie,
	getUsersDetails,
	refundAdminUserOrder,
} from "../../api/apiServiceFetch";
import { FILE_BASE_URL } from "../../config/env";
import { getCityLabelRu, getCountryLabelRu } from "../../utils/locationNames";
import "./Orders.scss";

const tabs = [
	{ key: "invests", label: "Инвестиции" },
	{ key: "orders", label: "Подписки" },
	{ key: "links", label: "Ссылки на оплату" },
];

const emptyLinkForm = {
	userCode: "",
	external: false,
	amount: "",
	description: "",
	comment: "",
};

const payTypeLabels = {
	0: "Банк",
	1: "TBank",
	3: "Stars",
	99: "Возврат",
};
const typeLabels = {
	1: "Подписка",
	2: "Огонь",
	3: "Чистый лист",
	5: "Повторная регистрация",
	10: "Инвестиция",
	20: "Ссылка на оплату",
	100: "Platinum",
};

const formatDate = (value) => {
	if (!value) return "-";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "-";

	return new Intl.DateTimeFormat("ru-RU", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	}).format(date);
};

const formatMoney = (value, currency) =>
	`${new Intl.NumberFormat("ru-RU", {
		minimumFractionDigits: 0,
		maximumFractionDigits: 2,
	}).format(Number(value || 0))} ${currency || ""}`.trim();

const orderName = (order) =>
	order.orderName || typeLabels[order.type] || `Order ${order.orderId}`;

const genderText = (value) => {
	if (value === 1) return "Мужчина";
	if (value === 2) return "Женщина";
	return "-";
};

const normalizeOrderResponse = (response) =>
	response?.order || response?.Order || null;

const normalizePhotos = (response) => {
	if (Array.isArray(response)) return response;
	if (Array.isArray(response?.data)) return response.data;
	if (Array.isArray(response?.Data)) return response.Data;
	return [];
};

const providerStatusLabels = {
	CONFIRMED: "Оплачен",
	AUTHORIZED: "Авторизован",
	LOCAL_PENDING: "Ожидает оплаты",
	NEW: "Создан",
	FORM_SHOWED: "Открыта форма оплаты",
	DEADLINE_EXPIRED: "Истек срок оплаты",
	CANCELED: "Отменен",
	REFUNDED: "Возвращен",
	PARTIAL_REFUNDED: "Частично возвращен",
};

const providerStatusText = (status) =>
	providerStatusLabels[status] || status || "-";

// navigator.clipboard недоступен на http-хостах, поэтому оставляем запасной путь.
const copyToClipboard = async (text) => {
	if (!text) return false;

	try {
		if (navigator?.clipboard?.writeText) {
			await navigator.clipboard.writeText(text);
			return true;
		}
	} catch {
		// падаем в запасной вариант ниже
	}

	try {
		const area = document.createElement("textarea");
		area.value = text;
		area.setAttribute("readonly", "");
		area.style.position = "fixed";
		area.style.opacity = "0";
		document.body.appendChild(area);
		area.select();
		const ok = document.execCommand("copy");
		document.body.removeChild(area);
		return ok;
	} catch {
		return false;
	}
};

const Orders = () => {
	const [activeTab, setActiveTab] = useState("invests");
	const [orders, setOrders] = useState([]);
	const [totalCount, setTotalCount] = useState(0);
	const [pageNumber, setPageNumber] = useState(1);
	const [searchTerm, setSearchTerm] = useState("");
	const [appliedSearch, setAppliedSearch] = useState("");
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [busyOrderId, setBusyOrderId] = useState(null);
	const [profileModal, setProfileModal] = useState(null);
	const [profileLoading, setProfileLoading] = useState(false);
	const [resultModal, setResultModal] = useState(null);
	const [linkForm, setLinkForm] = useState(null);
	const [linkSubmitting, setLinkSubmitting] = useState(false);
	const [createdLink, setCreatedLink] = useState(null);
	const [copyState, setCopyState] = useState("");

	const isLinksTab = activeTab === "links";
	const pageSize = 50;
	const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

	const totals = useMemo(() => {
		return orders.reduce(
			(acc, order) => {
				acc.count += 1;
				if (order.payState) {
					acc.paid += 1;
					acc.paidSum += Number(order.price || 0);
				} else acc.pending += 1;
				acc.sum += Number(order.price || 0);
				return acc;
			},
			{ count: 0, paid: 0, pending: 0, sum: 0, paidSum: 0 },
		);
	}, [orders]);

	useEffect(() => {
		const loadOrders = async () => {
			try {
				setLoading(true);
				setError("");
				const response = await getAdminUserOrders(
					activeTab,
					pageNumber,
					pageSize,
					appliedSearch,
				);

				setOrders(Array.isArray(response?.data) ? response.data : []);
				setTotalCount(Number(response?.totalCount || 0));
			} catch (err) {
				setError(err?.message || "Не удалось загрузить UserOrders.");
			} finally {
				setLoading(false);
			}
		};

		void loadOrders();
	}, [activeTab, pageNumber, appliedSearch]);

	const submitSearch = (event) => {
		event.preventDefault();
		setPageNumber(1);
		setAppliedSearch(searchTerm.trim());
	};

	const switchTab = (nextTab) => {
		setActiveTab(nextTab);
		setPageNumber(1);
	};

	const patchOrder = (nextOrder) => {
		if (!nextOrder?.id) return;
		setOrders((prev) =>
			prev.map((item) =>
				item.id === nextOrder.id ? { ...item, ...nextOrder } : item,
			),
		);
	};

	const removeOrder = (id) => {
		setOrders((prev) => prev.filter((item) => item.id !== id));
		setTotalCount((value) => Math.max(0, value - 1));
	};

	const showResult = ({ title, message, tone = "info", details = null }) => {
		setResultModal({ title, message, tone, details });
	};

	const handleCheckStatus = async (order) => {
		try {
			setBusyOrderId(order.id);
			const response = await checkAdminUserOrderStatus(order.id);
			const nextOrder = normalizeOrderResponse(response);
			const providerStatus =
				response?.providerStatus || response?.ProviderStatus;
			patchOrder(nextOrder);
			showResult({
				title: "Статус платежа",
				message: nextOrder?.payState
					? "Платеж подтвержден. Заказ обновлен."
					: "Платеж пока не подтвержден.",
				tone: nextOrder?.payState ? "success" : "warning",
				details: [
					["Заказ", `#${order.id}`],
					["Провайдер", payTypeLabels[order.payType] || order.payType],
					["Статус", providerStatusText(providerStatus)],
				],
			});
		} catch (err) {
			showResult({
				title: "Статус платежа",
				message: err?.message || "Не удалось проверить статус.",
				tone: "danger",
			});
		} finally {
			setBusyOrderId(null);
		}
	};

	const executeRefund = async (order) => {
		try {
			setBusyOrderId(order.id);
			const response = await refundAdminUserOrder(order.id);
			const nextOrder = normalizeOrderResponse(response);
			const providerStatus =
				response?.providerStatus || response?.ProviderStatus;
			patchOrder(nextOrder);
			showResult({
				title: "Возврат средств",
				message:
					response?.success || response?.Success
						? "Возврат выполнен, заказ отключен."
						: "Провайдер не принял запрос возврата.",
				tone: response?.success || response?.Success ? "success" : "warning",
				details: [
					["Заказ", `#${order.id}`],
					["Сумма", formatMoney(order.price, order.currency)],
					["Статус", providerStatusText(providerStatus)],
				],
			});
		} catch (err) {
			showResult({
				title: "Возврат средств",
				message: err?.message || "Не удалось сделать возврат.",
				tone: "danger",
			});
		} finally {
			setBusyOrderId(null);
		}
	};

	const handleRefund = (order) => {
		setResultModal({
			title: "Подтвердить возврат",
			message: `Вернуть средства по заказу #${order.id}?`,
			tone: "danger",
			details: [
				[
					"Пользователь",
					order.fullName || order.userName || `TgId ${order.tgId}`,
				],
				["Сумма", formatMoney(order.price, order.currency)],
				["Платеж", order.paymentId || "-"],
			],
			confirmText: "Вернуть",
			onConfirm: () => executeRefund(order),
		});
	};

	const executeDeleteUnpaid = async (order) => {
		try {
			setBusyOrderId(order.id);
			const response = await deleteUnpaidAdminUserOrder(order.id);
			if (response?.success || response?.Success) {
				removeOrder(order.id);
			}
			showResult({
				title: "Удаление заказа",
				message:
					response?.message ||
					response?.Message ||
					"Неоплаченный заказ удален.",
				tone: response?.success || response?.Success ? "success" : "warning",
			});
		} catch (err) {
			showResult({
				title: "Удаление заказа",
				message: err?.message || "Не удалось удалить неоплаченный заказ.",
				tone: "danger",
			});
		} finally {
			setBusyOrderId(null);
		}
	};

	const handleDeleteUnpaid = (order) => {
		setResultModal({
			title: "Удалить неоплаченный заказ",
			message: `Удалить заказ #${order.id}? Это действие уберет только неоплаченную запись.`,
			tone: "danger",
			details: [
				[
					"Пользователь",
					order.fullName || order.userName || `TgId ${order.tgId}`,
				],
				["Пакет", orderName(order)],
				["UUID", order.uuId || "-"],
			],
			confirmText: "Удалить",
			onConfirm: () => executeDeleteUnpaid(order),
		});
	};

	const openLinkForm = () => {
		setCopyState("");
		setCreatedLink(null);
		setLinkForm({ ...emptyLinkForm });
	};

	const updateLinkForm = (patch) =>
		setLinkForm((prev) => (prev ? { ...prev, ...patch } : prev));

	const submitLinkForm = async (event) => {
		event.preventDefault();
		if (!linkForm || linkSubmitting) return;

		const amount = Number(String(linkForm.amount).replace(",", "."));

		if (!Number.isFinite(amount) || amount <= 0) {
			showResult({
				title: "Ссылка на оплату",
				message: "Укажите сумму больше нуля.",
				tone: "warning",
			});
			return;
		}

		if (!linkForm.external && !String(linkForm.userCode).trim()) {
			showResult({
				title: "Ссылка на оплату",
				message:
					"Укажите код пользователя (ID или телеграм-код) либо отметьте «Внешний плательщик».",
				tone: "warning",
			});
			return;
		}

		try {
			setLinkSubmitting(true);
			const response = await createAdminPaymentLink({
				userCode: String(linkForm.userCode).trim(),
				external: linkForm.external,
				amount,
				description: linkForm.description.trim(),
				comment: linkForm.comment.trim(),
			});

			const payLink = response?.payLink || response?.PayLink;

			if (!payLink) {
				throw new Error(
					response?.message || response?.Message || "Ссылка не получена.",
				);
			}

			const nextOrder = normalizeOrderResponse(response);

			setLinkForm(null);
			setCopyState("");
			setCreatedLink({
				payLink,
				amount,
				order: nextOrder,
				external: linkForm.external,
				userCode: String(linkForm.userCode).trim(),
				description: linkForm.description.trim(),
			});

			if (isLinksTab && nextOrder) {
				setOrders((prev) => [nextOrder, ...prev]);
				setTotalCount((value) => value + 1);
			}
		} catch (err) {
			showResult({
				title: "Ссылка на оплату",
				message: err?.message || "Не удалось создать ссылку.",
				tone: "danger",
			});
		} finally {
			setLinkSubmitting(false);
		}
	};

	const handleCopyLink = async (payLink) => {
		const ok = await copyToClipboard(payLink);
		setCopyState(ok ? "ok" : "fail");
	};

	const handleCopyRowLink = async (order) => {
		const ok = await copyToClipboard(order.payLink);
		showResult({
			title: "Ссылка на оплату",
			message: ok
				? "Ссылка скопирована в буфер обмена."
				: "Не удалось скопировать. Скопируйте ссылку вручную.",
			tone: ok ? "success" : "warning",
			details: ok ? null : [["Ссылка", order.payLink || "-"]],
		});
	};

	const openProfile = async (order) => {
		if (!order.userId) {
			showResult({
				title: "Профиль недоступен",
				message: "У заказа нет userId, профиль открыть нельзя.",
				tone: "warning",
			});
			return;
		}

		setProfileModal({
			order,
			details: null,
			photos: [],
			selfie: null,
		});
		setProfileLoading(true);

		try {
			const [details, photos, selfie] = await Promise.all([
				getUsersDetails(order.userId).catch(() => null),
				getPhotos(order.userId).catch(() => []),
				getSelfie(order.userId).catch(() => null),
			]);

			setProfileModal({
				order,
				details,
				photos: normalizePhotos(photos),
				selfie,
			});
		} finally {
			setProfileLoading(false);
		}
	};

	return (
		<div className="orders-page">
			<header className="orders-header">
				<div>
					<h2>Покупки и Инвестиции</h2>
				</div>
				<div className="orders-header__tools">
					<button
						type="button"
						className="orders-create-link"
						onClick={openLinkForm}
					>
						+ Ссылка на оплату
					</button>
					<form className="orders-search" onSubmit={submitSearch}>
						<input
							value={searchTerm}
							onChange={(event) => setSearchTerm(event.target.value)}
							placeholder="Поиск: userId, tgId, имя, счет, paymentId, заметка"
						/>
						<button type="submit">Найти</button>
					</form>
				</div>
			</header>

			<div className="orders-tabs" role="tablist" aria-label="UserOrders tabs">
				{tabs.map((tab) => (
					<button
						key={tab.key}
						type="button"
						role="tab"
						aria-selected={activeTab === tab.key}
						className={activeTab === tab.key ? "active" : ""}
						onClick={() => switchTab(tab.key)}
					>
						{tab.label}
					</button>
				))}
			</div>

			<section className="orders-summary">
				<div>
					<span>Всего в выборке</span>
					<strong>{totalCount}</strong>
				</div>
				<div>
					<span>На странице</span>
					<strong>{totals.count}</strong>
				</div>
				<div>
					<span>Оплачено</span>
					<strong>{totals.paid}</strong>
				</div>
				<div>
					<span>Ожидают оплаты</span>
					<strong>{totals.pending}</strong>
				</div>
				{isLinksTab ? (
					<div>
						<span>Оплачено на сумму</span>
						<strong>{formatMoney(totals.paidSum, "RUB")}</strong>
					</div>
				) : null}
			</section>

			{error ? (
				<div className="orders-state orders-state--error">{error}</div>
			) : null}

			{isLinksTab ? (
				<div className="orders-table-wrap">
					<table className="orders-table">
						<thead>
							<tr>
								<th>ID</th>
								<th>Плательщик</th>
								<th>Назначение</th>
								<th>Сумма</th>
								<th>Статус</th>
								<th>Создана</th>
								<th>Оплачена</th>
								<th>Счет</th>
								<th>Действия</th>
							</tr>
						</thead>
						<tbody>
							{loading ? (
								<tr>
									<td colSpan={9} className="orders-empty">
										Загружаем...
									</td>
								</tr>
							) : orders.length === 0 ? (
								<tr>
									<td colSpan={9} className="orders-empty">
										Ссылок пока нет
									</td>
								</tr>
							) : (
								orders.map((order) => {
									const isRefunded = Number(order.payType) === 99;
									const isExternal = !order.tgId;

									return (
										<tr key={order.id}>
											<td data-label="ID">{order.id}</td>
											<td data-label="Плательщик">
												<div className="orders-user">
													<strong>
														{isExternal
															? "Внешний плательщик"
															: order.fullName || order.userName || "Без имени"}
													</strong>
													<span>
														{isExternal
															? "не привязан"
															: `${order.userId ? `ID ${order.userId}, ` : ""}код ${order.tgId}`}
													</span>
												</div>
											</td>
											<td data-label="Назначение">{order.comment || "-"}</td>
											<td data-label="Сумма">
												{formatMoney(order.price, order.currency || "RUB")}
											</td>
											<td data-label="Статус">
												<span
													className={
														isRefunded
															? "orders-status refunded"
															: order.payState
																? "orders-status paid"
																: "orders-status pending"
													}
												>
													{isRefunded
														? "Возврат"
														: order.payState
															? "Оплачена"
															: "Ожидает"}
												</span>
											</td>
											<td data-label="Создана">{formatDate(order.createDate)}</td>
											<td data-label="Оплачена">{formatDate(order.payDate)}</td>
											<td data-label="Счет" className="orders-uuid">
												{order.uuId || "-"}
											</td>
											<td data-label="Действия">
												<div className="orders-actions">
													<button
														type="button"
														disabled={!order.payLink}
														onClick={() => handleCopyRowLink(order)}
													>
														Копировать
													</button>
													<button
														type="button"
														disabled={busyOrderId === order.id || isRefunded}
														onClick={() => handleCheckStatus(order)}
													>
														Проверить
													</button>
													{order.userId ? (
														<button
															type="button"
															onClick={() => openProfile(order)}
														>
															Профиль
														</button>
													) : null}
													{order.payState && !isRefunded ? (
														<button
															type="button"
															className="danger"
															disabled={busyOrderId === order.id}
															onClick={() => handleRefund(order)}
														>
															Возврат
														</button>
													) : null}
													{!order.payState && !isRefunded ? (
														<button
															type="button"
															className="danger"
															disabled={busyOrderId === order.id}
															onClick={() => handleDeleteUnpaid(order)}
														>
															Удалить
														</button>
													) : null}
												</div>
											</td>
										</tr>
									);
								})
							)}
						</tbody>
					</table>
				</div>
			) : (
				<div className="orders-table-wrap">
					<table className="orders-table">
						<thead>
							<tr>
								<th>ID</th>
								<th>Пользователь</th>
								<th>TgId</th>
								<th>Пакет</th>
								<th>Цена</th>
								<th>Оплата</th>
								<th>Статус</th>
								<th>Создан</th>
								<th>Оплачен</th>
								<th>До</th>
								<th>UUID</th>
								<th>Действия</th>
							</tr>
						</thead>
						<tbody>
							{loading ? (
								<tr>
									<td colSpan={12} className="orders-empty">
										Загружаем...
									</td>
								</tr>
							) : orders.length === 0 ? (
								<tr>
									<td colSpan={12} className="orders-empty">
										Записи не найдены
									</td>
								</tr>
							) : (
								orders.map((order) => {
									const isPremiumGift =
										String(order.uuId || "").toLowerCase() === "premium";
									const isRefunded = Number(order.payType) === 99;

									return (
										<tr key={order.id}>
											<td data-label="ID">{order.id}</td>
											<td data-label="Пользователь">
												<div className="orders-user">
													<strong>
														{order.fullName || order.userName || "-"}
													</strong>
													<span>
														{order.userId ? `ID ${order.userId}` : "нет userId"}
														{order.country
															? `, ${getCountryLabelRu(order.country)}`
															: ""}
													</span>
												</div>
											</td>
											<td data-label="TgId">{order.tgId}</td>
											<td data-label="Пакет">
												<div className="orders-user">
													<strong>{orderName(order)}</strong>
													<span>
														type {order.type}, order {order.orderId}
													</span>
												</div>
											</td>
											<td data-label="Цена">
												{formatMoney(order.price, order.currency)}
											</td>
											<td data-label="Оплата">
												{payTypeLabels[order.payType] || order.payType}
											</td>
											<td data-label="Статус">
												<span
													className={
														isPremiumGift
															? "orders-status gift"
															: isRefunded
																? "orders-status refunded"
																: order.payState
																	? "orders-status paid"
																	: "orders-status pending"
													}
												>
													{isPremiumGift
														? "Подарок"
														: isRefunded
															? "Возврат"
															: order.payState
																? "Оплачен"
																: "Ожидает"}
												</span>
											</td>
											<td data-label="Создан">{formatDate(order.createDate)}</td>
											<td data-label="Оплачен">{formatDate(order.payDate)}</td>
											<td data-label="До">{formatDate(order.endDate)}</td>
											<td data-label="UUID" className="orders-uuid">
												{order.uuId || "-"}
											</td>
											<td data-label="Действия">
												<div className="orders-actions">
													<button
														type="button"
														onClick={() => openProfile(order)}
													>
														Профиль
													</button>
													{!isPremiumGift ? (
														<>
															{!order.payState && order.payType !== 99 && (
																<button
																	type="button"
																	disabled={
																		busyOrderId === order.id || order.payState
																	}
																	onClick={() => handleCheckStatus(order)}
																>
																	Статус
																</button>
															)}
															{!isRefunded && (
																<button
																	type="button"
																	className="danger"
																	disabled={
																		busyOrderId === order.id ||
																		!order.payState ||
																		order.payType !== 1
																	}
																	onClick={() => handleRefund(order)}
																>
																	Возврат
																</button>
															)}
														</>
													) : (
														<button
															type="button"
															className="danger"
															disabled={busyOrderId === order.id}
															onClick={() => handleDeleteUnpaid(order)}
														>
															Удалить
														</button>
													)}
												</div>
											</td>
										</tr>
									);
								})
							)}
						</tbody>
					</table>
				</div>
			)}

			<footer className="orders-pagination">
				<button
					type="button"
					disabled={pageNumber <= 1 || loading}
					onClick={() => setPageNumber((value) => Math.max(1, value - 1))}
				>
					Назад
				</button>
				<span>
					Страница {pageNumber} из {totalPages}
				</span>
				<button
					type="button"
					disabled={pageNumber >= totalPages || loading}
					onClick={() => setPageNumber((value) => value + 1)}
				>
					Вперед
				</button>
			</footer>

			{linkForm ? (
				<div
					className="orders-dialog"
					onMouseDown={() => (linkSubmitting ? null : setLinkForm(null))}
				>
					<form
						className="orders-dialog__body orders-linkform"
						onMouseDown={(event) => event.stopPropagation()}
						onSubmit={submitLinkForm}
					>
						<h3>Новая ссылка на оплату</h3>
						<p>
							Отдельный платеж на произвольную сумму. Подписка, огни и чистые
							листы после оплаты не начисляются — платеж только фиксируется.
						</p>

						<label className="orders-linkform__field">
							<span>Сумма, ₽</span>
							<input
								type="number"
								min="1"
								step="0.01"
								inputMode="decimal"
								value={linkForm.amount}
								autoFocus
								onChange={(event) =>
									updateLinkForm({ amount: event.target.value })
								}
								placeholder="Например, 2500"
							/>
						</label>

						<label className="orders-linkform__field">
							<span>Код пользователя</span>
							<input
								value={linkForm.userCode}
								disabled={linkForm.external}
								inputMode="numeric"
								onChange={(event) =>
									updateLinkForm({
										userCode: event.target.value.replace(/[^0-9]/g, ""),
									})
								}
								placeholder="ID из админки или телеграм-код"
							/>
						</label>

						<label className="orders-linkform__check">
							<input
								type="checkbox"
								checked={linkForm.external}
								onChange={(event) =>
									updateLinkForm({ external: event.target.checked })
								}
							/>
							Внешний плательщик (нет в базе)
						</label>

						<label className="orders-linkform__field">
							<span>Назначение платежа</span>
							<input
								value={linkForm.description}
								maxLength={250}
								onChange={(event) =>
									updateLinkForm({ description: event.target.value })
								}
								placeholder="Видно плательщику на форме оплаты и в чеке"
							/>
						</label>

						<label className="orders-linkform__field">
							<span>Заметка для админки</span>
							<input
								value={linkForm.comment}
								maxLength={500}
								onChange={(event) =>
									updateLinkForm({ comment: event.target.value })
								}
								placeholder="Кому выставлена ссылка, за что"
							/>
						</label>

						<div className="orders-dialog__actions">
							<button
								type="button"
								className="secondary"
								disabled={linkSubmitting}
								onClick={() => setLinkForm(null)}
							>
								Отмена
							</button>
							<button type="submit" disabled={linkSubmitting}>
								{linkSubmitting ? "Создаем..." : "Создать ссылку"}
							</button>
						</div>
					</form>
				</div>
			) : null}

			{createdLink ? (
				<div className="orders-dialog" onMouseDown={() => setCreatedLink(null)}>
					<div
						className="orders-dialog__body success orders-linkresult"
						onMouseDown={(event) => event.stopPropagation()}
					>
						<div className="orders-dialog__mark" />
						<h3>Ссылка на оплату готова</h3>
						<p>Скопируйте ссылку и отправьте ее человеку.</p>

						<div className="orders-dialog__details">
							<div>
								<span>Сумма</span>
								<strong>{formatMoney(createdLink.amount, "RUB")}</strong>
							</div>
							<div>
								<span>Плательщик</span>
								<strong>
									{createdLink.external
										? "Внешний"
										: createdLink.order?.fullName ||
											createdLink.order?.userName ||
											`код ${createdLink.userCode}`}
								</strong>
							</div>
							<div>
								<span>Счет</span>
								<strong>{createdLink.order?.uuId || "-"}</strong>
							</div>
						</div>

						<div className="orders-linkresult__link">
							<input readOnly value={createdLink.payLink} onFocus={(event) => event.target.select()} />
							<button
								type="button"
								onClick={() => handleCopyLink(createdLink.payLink)}
							>
								{copyState === "ok" ? "Скопировано" : "Копировать"}
							</button>
						</div>

						{copyState === "fail" ? (
							<p className="orders-linkresult__hint">
								Браузер не дал скопировать автоматически — выделите ссылку и
								скопируйте вручную.
							</p>
						) : null}

						<div className="orders-dialog__actions">
							<button
								type="button"
								className="secondary"
								onClick={() => setCreatedLink(null)}
							>
								Закрыть
							</button>
							<button
								type="button"
								onClick={() => {
									setCreatedLink(null);
									if (!isLinksTab) switchTab("links");
								}}
							>
								К списку ссылок
							</button>
						</div>
					</div>
				</div>
			) : null}

			{resultModal ? (
				<div className="orders-dialog" onMouseDown={() => setResultModal(null)}>
					<div
						className={`orders-dialog__body ${resultModal.tone || "info"}`}
						onMouseDown={(event) => event.stopPropagation()}
					>
						<div className="orders-dialog__mark" />
						<h3>{resultModal.title}</h3>
						<p>{resultModal.message}</p>

						{Array.isArray(resultModal.details) &&
						resultModal.details.length > 0 ? (
							<div className="orders-dialog__details">
								{resultModal.details.map(([label, value]) => (
									<div key={label}>
										<span>{label}</span>
										<strong>{value}</strong>
									</div>
								))}
							</div>
						) : null}

						<div className="orders-dialog__actions">
							<button
								type="button"
								className="secondary"
								onClick={() => setResultModal(null)}
							>
								{resultModal.onConfirm ? "Отмена" : "Закрыть"}
							</button>
							{resultModal.onConfirm ? (
								<button
									type="button"
									className="danger"
									onClick={() => {
										const confirm = resultModal.onConfirm;
										setResultModal(null);
										confirm();
									}}
								>
									{resultModal.confirmText || "Подтвердить"}
								</button>
							) : null}
						</div>
					</div>
				</div>
			) : null}

			{profileModal ? (
				<div className="orders-modal" onMouseDown={() => setProfileModal(null)}>
					<div
						className="orders-modal__body"
						onMouseDown={(event) => event.stopPropagation()}
					>
						<header>
							<div>
								<h3>
									{profileModal.order.fullName ||
										profileModal.order.userName ||
										"Профиль"}
								</h3>
								<p>
									ID {profileModal.order.userId || "-"} · TgId{" "}
									{profileModal.order.tgId}
								</p>
							</div>
							<button type="button" onClick={() => setProfileModal(null)}>
								×
							</button>
						</header>

						{profileLoading ? (
							<div className="orders-empty">Загружаем профиль...</div>
						) : (
							<div className="orders-profile">
								<section className="orders-profile__media">
									{profileModal.selfie?.fileName ? (
										<img
											src={`${FILE_BASE_URL}/Selfie/${profileModal.order.userId}/${profileModal.selfie.fileName}`}
											alt="Селфи"
										/>
									) : null}

									{profileModal.photos.map((photo) => (
										<img
											key={photo.id || photo.fileName}
											src={`${FILE_BASE_URL}/Photo/${profileModal.order.userId}/${photo.fileName}`}
											alt="Фото профиля"
										/>
									))}

									{!profileModal.selfie?.fileName &&
									profileModal.photos.length === 0 ? (
										<div className="orders-empty">Фото нет</div>
									) : null}
								</section>

								<section className="orders-profile__info">
									<div>
										<span>Пол</span>
										<strong>{genderText(profileModal.order.gender)}</strong>
									</div>
									<div>
										<span>Страна</span>
										<strong>
											{getCountryLabelRu(profileModal.order.country)}
										</strong>
									</div>
									<div>
										<span>Город</span>
										<strong>
											{getCityLabelRu(
												profileModal.order.country,
												profileModal.order.city,
											)}
										</strong>
									</div>
									<div>
										<span>Язык</span>
										<strong>{profileModal.order.language || "-"}</strong>
									</div>
									<div>
										<span>Дата рождения</span>
										<strong>{formatDate(profileModal.order.birthday)}</strong>
									</div>
									<div>
										<span>Регистрация</span>
										<strong>
											{formatDate(profileModal.order.userCreateDate)}
										</strong>
									</div>
									<div>
										<span>Последний вход</span>
										<strong>
											{formatDate(profileModal.order.lastLoginDate)}
										</strong>
									</div>
									<div>
										<span>Модерация</span>
										<strong>
											{profileModal.order.moderated ? "Да" : "Нет"}
										</strong>
									</div>
									<div>
										<span>Активен</span>
										<strong>
											{profileModal.order.userIsActive ? "Да" : "Нет"}
										</strong>
									</div>
									<div>
										<span>Профессия</span>
										<strong>{profileModal.details?.profession || "-"}</strong>
									</div>
									<div>
										<span>Цель</span>
										<strong>{profileModal.details?.goal || "-"}</strong>
									</div>
									<div>
										<span>Рост</span>
										<strong>{profileModal.details?.height || "-"}</strong>
									</div>
									<div className="wide">
										<span>О себе</span>
										<strong>{profileModal.details?.description || "-"}</strong>
									</div>
									<div className="wide">
										<span>Мировоззрение</span>
										<strong>{profileModal.details?.worldview || "-"}</strong>
									</div>
									<div className="wide">
										<span>Языки</span>
										<strong>{profileModal.details?.languages || "-"}</strong>
									</div>
									<div className="wide">
										<span>Заказ</span>
										<strong>
											{orderName(profileModal.order)} ·{" "}
											{formatMoney(
												profileModal.order.price,
												profileModal.order.currency,
											)}{" "}
											· {profileModal.order.payState ? "оплачен" : "ожидает"}
										</strong>
									</div>
								</section>
							</div>
						)}
					</div>
				</div>
			) : null}
		</div>
	);
};

export default Orders;
